import { MetricField, type MyLensesOrganicStatsResponseEntry } from "../../gql-generated/types";
import { ComputedMetricField, type InsightsMetricsMap, LensGranularity, type StatsItem } from "../models";

import { mapAverageStatData } from "./mapAverageStatData";
import { mapFieldsToLifetimeTotalValues } from "./mapFieldsToLifetimeTotalValues";
import { mapLensCTRData } from "./mapLensCTRData";

interface MapMetricComputedProps {
    metrics: InsightsMetricsMap<StatsItem>;
    metricsPrevious: InsightsMetricsMap<StatsItem>;
    lifetimeData: readonly MyLensesOrganicStatsResponseEntry[];
    selectedTimeRange: LensGranularity;
    computed: InsightsMetricsMap<StatsItem>;
}

export const mapMetricsComputed = ({
    metrics,
    metricsPrevious,
    lifetimeData,
    selectedTimeRange,
    computed,
}: MapMetricComputedProps): InsightsMetricsMap<StatsItem> => {
    const lensCtrLifetimeTotals =
        selectedTimeRange === LensGranularity.Lifetime
            ? mapFieldsToLifetimeTotalValues([MetricField.SwipeUps, MetricField.LensPlays], lifetimeData)
            : undefined;
    const averageLensFaceFoundLifetimeTotals =
        selectedTimeRange === LensGranularity.Lifetime
            ? mapFieldsToLifetimeTotalValues([MetricField.LensFaceFound, MetricField.LensPlays], lifetimeData)
            : undefined;
    const averageLensFaceLostLifetimeTotals =
        selectedTimeRange === LensGranularity.Lifetime
            ? mapFieldsToLifetimeTotalValues([MetricField.LensFaceLost, MetricField.LensPlays], lifetimeData)
            : undefined;
    const averageLensObjectTrackedLifetimeTotals =
        selectedTimeRange === LensGranularity.Lifetime
            ? mapFieldsToLifetimeTotalValues([MetricField.LensObjectTracked, MetricField.LensPlays], lifetimeData)
            : undefined;
    const averageLensObjectTracked3DLifetimeTotals =
        selectedTimeRange === LensGranularity.Lifetime
            ? mapFieldsToLifetimeTotalValues([MetricField.LensObjectTracked_3D, MetricField.LensPlays], lifetimeData)
            : undefined;
    const averageLensMarkerTrackedLifetimeTotals =
        selectedTimeRange === LensGranularity.Lifetime
            ? mapFieldsToLifetimeTotalValues([MetricField.LensMarkerTracked, MetricField.LensPlays], lifetimeData)
            : undefined;
    const averageLensTapLifetimeTotals =
        selectedTimeRange === LensGranularity.Lifetime
            ? mapFieldsToLifetimeTotalValues([MetricField.LensTap, MetricField.LensPlays], lifetimeData)
            : undefined;

    return {
        ...computed,
        [ComputedMetricField.LensCTR]: mapLensCTRData(
            metrics,
            metricsPrevious,
            computed[ComputedMetricField.LensCTR],
            lensCtrLifetimeTotals,
        ),
        [ComputedMetricField.AverageLensFaceFound]: mapAverageStatData(
            metrics,
            metricsPrevious,
            computed[ComputedMetricField.AverageLensFaceFound],
            MetricField.LensFaceFound,
            averageLensFaceFoundLifetimeTotals,
        ),
        [ComputedMetricField.AverageLensFaceLost]: mapAverageStatData(
            metrics,
            metricsPrevious,
            computed[ComputedMetricField.AverageLensFaceLost],
            MetricField.LensFaceLost,
            averageLensFaceLostLifetimeTotals,
        ),
        [ComputedMetricField.AverageLensObjectTracked]: mapAverageStatData(
            metrics,
            metricsPrevious,
            computed[ComputedMetricField.AverageLensObjectTracked],
            MetricField.LensObjectTracked,
            averageLensObjectTrackedLifetimeTotals,
        ),
        [ComputedMetricField.AverageLensObjectTracked_3D]: mapAverageStatData(
            metrics,
            metricsPrevious,
            computed[ComputedMetricField.AverageLensObjectTracked_3D],
            MetricField.LensObjectTracked_3D,
            averageLensObjectTracked3DLifetimeTotals,
        ),
        [ComputedMetricField.AverageLensMarkerTracked]: mapAverageStatData(
            metrics,
            metricsPrevious,
            computed[ComputedMetricField.AverageLensMarkerTracked],
            MetricField.LensMarkerTracked,
            averageLensMarkerTrackedLifetimeTotals,
        ),
        [ComputedMetricField.AverageLensTap]: mapAverageStatData(
            metrics,
            metricsPrevious,
            computed[ComputedMetricField.AverageLensTap],
            MetricField.LensTap,
            averageLensTapLifetimeTotals,
        ),
    };
};
